import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, CheckCircle2, X, Loader2, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

/**
 * Aggregated pick-list generator for the "الفواتير المؤكدة" tab.
 *
 * Admin selects multiple confirmed invoices (checkbox mode), presses
 * "طباعة قائمة التجهيز" and gets ONE consolidated sheet aggregating all
 * items across the selected orders (product image + name + total qty).
 * NO Jumlaty logo, NO prices — this is a warehouse pick-list, not an
 * invoice. After printing, the selected orders are marked
 * `picklist_printed_at` on the server so their card tint switches from
 * red (needs picking) → green (already picked). Feb 17, 2026 request.
 */
const AggregatePickListButton = ({ selectMode, setSelectMode, selectedIds, orders, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // aggregated items to print

  useEffect(() => {
    if (!selectMode) setPreview(null);
  }, [selectMode]);

  const aggregateAndPrint = async () => {
    if (selectedIds.length === 0) {
      toast.info("اختر فاتورة واحدة على الأقل");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/picklist/aggregate`, {
        order_ids: selectedIds,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التجميع");
    } finally {
      setBusy(false);
    }
  };

  const confirmPrint = async () => {
    setBusy(true);
    try {
      // Mark orders as printed so the UI turns green
      await axios.post(`${API}/admin/orders/picklist/mark-printed`, {
        order_ids: selectedIds,
      });
      // Open print window
      const w = window.open("", "_blank");
      w.document.write(buildPickListHtml(preview, selectedIds.length));
      w.document.close();
      toast.success(`✓ تم إرسال قائمة تجهيز ${selectedIds.length} فاتورة للطباعة`);
      setPreview(null);
      setSelectMode(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التسجيل");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Toggle button — enters/exits selection mode */}
      <button
        onClick={() => setSelectMode(!selectMode)}
        data-testid="picklist-select-toggle"
        className={`text-sm font-black rounded-full px-4 py-2 flex items-center gap-2 transition-colors ${
          selectMode
            ? "bg-[#B91C1C] hover:bg-[#991b1b] text-white"
            : "bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
        }`}
      >
        {selectMode ? (<><X className="w-4 h-4" /> إلغاء التحديد</>)
                    : (<><CheckCircle2 className="w-4 h-4" /> تحديد فواتير</>)}
      </button>

      {/* Sticky action bar shown when at least one order is selected */}
      {selectMode && selectedIds.length > 0 && (
        <div className="fixed bottom-16 inset-x-0 z-[100] p-2 pointer-events-none">
          <div className="max-w-2xl mx-auto bg-gradient-to-l from-[#15803D] to-[#22c55e] text-white rounded-2xl shadow-2xl border-2 border-white/30 flex items-center gap-2 p-3 pointer-events-auto">
            <div className="flex-1">
              <p className="font-black text-lg" dir="ltr">{selectedIds.length}</p>
              <p className="text-[10px] opacity-90">فاتورة محدّدة</p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(`إرسال ${selectedIds.length} فاتورة (مفردة) للمُجهّز؟`)) return;
                setBusy(true);
                try {
                  await axios.post(`${API}/admin/orders/send-to-mujahez`, {
                    order_ids: selectedIds, is_aggregated: false,
                  });
                  toast.success(`✓ أُرسِلت ${selectedIds.length} فاتورة للمُجهّز`);
                  setSelectMode(false);
                  onDone && onDone();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "فشل الإرسال");
                } finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="send-to-mujahez-individual"
              className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white font-black rounded-full px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-60"
            >
              📤 للمُجهّز (مفرد)
            </button>
            <button
              onClick={async () => {
                if (!window.confirm(`إرسال ${selectedIds.length} فاتورة كقائمة تجهيز مُجمّعة للمُجهّز؟`)) return;
                setBusy(true);
                try {
                  await axios.post(`${API}/admin/orders/send-to-mujahez`, {
                    order_ids: selectedIds, is_aggregated: true,
                  });
                  toast.success(`✓ أُرسِلت قائمة مُجمّعة من ${selectedIds.length} فاتورة`);
                  setSelectMode(false);
                  onDone && onDone();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "فشل الإرسال");
                } finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="send-to-mujahez-aggregated"
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-black rounded-full px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-60"
            >
              📦 للمُجهّز (مُجمّعة)
            </button>
            <button
              onClick={aggregateAndPrint}
              disabled={busy}
              data-testid="picklist-print-btn"
              className="bg-white text-[#15803D] font-black rounded-full px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              اطبع الآن
            </button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-3"
             onClick={() => setPreview(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 pb-2 border-b flex justify-between items-center">
              <div>
                <h3 className="font-black text-[#7C3AED] text-base flex items-center gap-2">
                  <Package className="w-5 h-5" /> معاينة قائمة التجهيز
                </h3>
                <p className="text-[11px] text-[#5C6E64]">
                  {preview.items?.length || 0} صنف · مجمّع من {selectedIds.length} فاتورة
                </p>
              </div>
              <button onClick={() => setPreview(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {(preview.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#F8FAFC] rounded-xl p-2 border border-[#E4EBE6]">
                  <div className="w-14 h-14 bg-white rounded-lg overflow-hidden shrink-0 flex items-center justify-center border border-[#E4EBE6]">
                    {it.image_path
                      ? <img src={`${API}/files/${it.image_path}`} alt="" className="w-full h-full object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                      : <Package className="w-6 h-6 text-[#5C6E64]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1E3A5F] truncate">{it.name_ar || it.name}</p>
                    {it.units_per_carton && (
                      <p className="text-[10px] text-[#5C6E64]">
                        الكرتون: {it.units_per_carton} قطعة · <span dir="ltr">{Math.floor(it.quantity / it.units_per_carton)} كرتون + {it.quantity % it.units_per_carton} قطعة</span>
                      </p>
                    )}
                  </div>
                  <div className="text-center bg-[#FEF3C7] rounded-lg px-3 py-1 border-2 border-[#F59E0B]/40 shrink-0">
                    <p className="font-black text-2xl text-[#78350F]" dir="ltr">{fmt(it.quantity)}</p>
                    <p className="text-[9px] font-bold text-[#78350F]">قطعة</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex gap-2">
              <button onClick={() => setPreview(null)} className="flex-1 bg-[#F3F6F4] text-[#5C6E64] font-bold rounded-full py-2">إلغاء</button>
              <button
                onClick={confirmPrint}
                disabled={busy}
                data-testid="picklist-confirm-print"
                className="flex-1 bg-[#15803D] hover:bg-[#0f6630] disabled:opacity-60 text-white font-black rounded-full py-2 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                اطبع الآن
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Standalone print HTML — matches user requirement: NO logo, no prices,
// image + name + total qty. Pretty rows with alternating background.
function buildPickListHtml(preview, invoiceCount) {
  const items = preview.items || [];
  const rows = items.map((it, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td class="c img">
        ${it.image_path
          ? `<img src="${process.env.REACT_APP_BACKEND_URL}/api/files/${it.image_path}" class="pi" onerror="this.style.display='none'"/>`
          : "—"}
      </td>
      <td class="r name">${(it.name_ar || it.name || "").replace(/</g, "&lt;")}</td>
      <td class="c qty">${Number(it.quantity || 0).toLocaleString("en-US")}</td>
      <td class="c cartons">${
        it.units_per_carton
          ? `${Math.floor(it.quantity / it.units_per_carton)} + ${it.quantity % it.units_per_carton}`
          : "—"
      }</td>
    </tr>`).join("");
  const now = new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
<title>قائمة تجهيز — ${invoiceCount} فاتورة</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 8mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Tajawal',sans-serif; color: #000; margin: 0; }
  .hd {
    background: #7C3AED; color: #fff; padding: 8px 12px; border-radius: 10px;
    margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center;
  }
  .hd h1 { margin: 0; font-size: 18px; font-weight: 900; }
  .hd .sub { font-size: 11px; opacity: .9; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1.5px solid #000; padding: 6px; }
  th { background: #1E3A5F; color: #fff; font-weight: 900; }
  td.c { text-align: center; }
  td.r { text-align: right; }
  td.name { font-weight: 800; font-size: 14px; }
  td.qty { font-weight: 900; font-size: 20px; background: #FEF08A; color: #78350F; }
  td.cartons { font-family: 'Courier New',monospace; font-size: 12px; }
  td.img { padding: 3px; width: 60px; }
  .pi { width: 44px; height: 44px; object-fit: contain; display: block; margin: 0 auto; }
  tbody tr:nth-child(even) td:not(.qty) { background: #F8FAFC; }
  .foot { margin-top: 8px; font-size: 10px; color: #5C6E64; display: flex; justify-content: space-between; }
</style></head><body>
<div class="hd">
  <div><h1>قائمة تجهيز</h1><div class="sub">${invoiceCount} فاتورة · ${items.length} صنف</div></div>
  <div class="sub">${now}</div>
</div>
<table>
  <thead>
    <tr>
      <th style="width:32px">ت</th>
      <th style="width:60px">صورة</th>
      <th>المادة</th>
      <th style="width:90px">العدد</th>
      <th style="width:80px">كراتين + قطع</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
<div class="foot">
  <span>✓ عند الانتهاء، ضع علامة ✔ بجانب كل صنف</span>
  <span>جُملَتي — إدارة المخزن</span>
</div>
<script>window.onload=()=>setTimeout(()=>window.print(),500);<\/script>
</body></html>`;
}

export default AggregatePickListButton;
